import { useMutation, useQuery } from '@apollo/client';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { DateTime } from 'luxon';
import { filter } from 'ramda';
import React, { FC, useContext } from 'react';
import { useDeepCompareEffect } from 'react-use';

import LoadingWrapper from '@ui/atoms/LoadingWrapper';

import { IAuthenticatedMember, SubscriptionInfo } from '@hooks/useMemberInfo';

import { keysToCamel } from '@utils/butter';
import { doesNotExist, exists } from '@utils/general';
import { combineMemberFragments } from '@utils/graphql';

import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IWaivable } from '@data/insurance';

import { IPageProps } from '@folx-types/flow-steps';
import { IUpcomingAppointment, IUpcomingAppointments } from '@folx-types/scheduledAppointments';

import { membership, membershipName, queryName } from '@graphql/fragments/membership';
import { waivable, waivableName } from '@graphql/fragments/waivable';
import CANCEL_MEMBER_SUBSCRIPTION_MUTATION from '@graphql/mutations/Subscription/CancelMemberSubscription.mutation.gql';
import UPCOMING_APPOINTMENTS_QUERY from '@graphql/queries/UpcomingAppointments.query.gql';

import CancelationConfirmation, {
	ICancelationConfirmationPageContent,
} from './CancelationConfirmation.component';

interface ICancelationConfirmationPageProps extends IPageProps {}

const CancelationConfirmationPage: FC<ICancelationConfirmationPageProps> = ({ path }) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "cancelation-confirmation" }) {
				membership_header
				membership_info
				trial_header
				trial_info
				benefits_header
				benefits_topics {
					info
				}
				keep_trial_label
				nevermind_label
				cancel_label
				support_cancel
			}
		}
	`);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const { data: membershipData, loading: membershipLoading } = useQuery<
		IAuthenticatedMember<SubscriptionInfo & IWaivable>
	>(combineMemberFragments([membershipName, waivableName], [membership, waivable], queryName), {
		client: graphQLv2Client,
	});

	const [cancelMembership] = useMutation(CANCEL_MEMBER_SUBSCRIPTION_MUTATION, {
		client: graphQLv2Client,
	});

	const { loading: loadingUpcomingAppointments, data: upcomingAppointmentsData } =
		useQuery<IUpcomingAppointments>(UPCOMING_APPOINTMENTS_QUERY, { fetchPolicy: 'network-only' });

	useDeepCompareEffect(() => {
		const upcomingAppointments =
			upcomingAppointmentsData?.upcomingAppointmentsForCurrentMember?.nodes;
		const subscriptionRenewalDate =
			membershipData?.authenticatedMember?.membershipSubscription?.nextSubscriptionRenewalDate;
		if (
			doesNotExist(upcomingAppointments) ||
			doesNotExist(subscriptionRenewalDate) ||
			membershipLoading ||
			loadingUpcomingAppointments
		) {
			return;
		}

		const appointmentsPastCancelDate = filter<IUpcomingAppointment>(
			(appointment) =>
				DateTime.fromISO(appointment.scheduledDt) > DateTime.fromISO(subscriptionRenewalDate)
		)(upcomingAppointments ?? []);

		if (exists(appointmentsPastCancelDate)) {
			navigate('../appointments_past_cancel_date', {
				state: { upcomingAppointments: appointmentsPastCancelDate },
			});
		}
	}, [membershipLoading, loadingUpcomingAppointments, membershipData, upcomingAppointmentsData]);

	const refinedContent: ICancelationConfirmationPageContent = keysToCamel(
		pageContent
	) as ICancelationConfirmationPageContent;

	return (
		<LoadingWrapper loading={membershipLoading || loadingUpcomingAppointments}>
			<CancelationConfirmation
				pageContent={refinedContent}
				membershipSubscription={membershipData?.authenticatedMember?.membershipSubscription}
				membershipWaived={!!membershipData?.authenticatedMember?.isMembershipWaivable?.isWaivable}
				cancelMembership={cancelMembership}
			/>
		</LoadingWrapper>
	);
};

export default CancelationConfirmationPage;
